import { Link } from "@tanstack/react-router";
import { Mail, ArrowLeft, Send } from "lucide-react";
import { useState } from "react";
import { toast } from "@/shared/components";
import { supabase } from "@/shared/lib/supabase";
import { Input, Label } from "@/shared/components";
import { AuthTemplate } from "@modootoday/ui-widgets/templates";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) throw error;

      setSent(true);
      toast.success("비밀번호 재설정 메일이 발송되었습니다");
    } catch (err: any) {
      toast.error(err.message || "메일 발송에 실패했습니다");
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthTemplate
        title="메일을 확인하세요"
        subtitle={
          <>
            <span className="font-semibold text-text-primary">{email}</span>으로
            비밀번호 재설정 링크를 보냈습니다. 메일함을 확인해주세요.
          </>
        }
      >
        <div className="flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-accent/10">
            <Mail className="size-5 text-text-brand" />
          </div>
        </div>
        <p className="text-center text-[12px] text-text-tertiary">
          메일이 도착하지 않았다면 스팸 폴더를 확인하거나,{" "}
          <button
            onClick={() => {
              setSent(false);
              setEmail("");
            }}
            className="font-semibold text-text-brand hover:underline"
          >
            다시 시도
          </button>
          해주세요.
        </p>
        <Link
          to="/auth/login"
          className="inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-text-brand hover:underline"
        >
          <ArrowLeft className="size-3.5" /> 로그인으로 돌아가기
        </Link>
      </AuthTemplate>
    );
  }

  return (
    <AuthTemplate
      title="비밀번호 찾기"
      subtitle="가입 시 사용한 이메일을 입력하시면 비밀번호 재설정 링크를 보내드립니다."
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label className="mb-1.5 block">이메일</Label>
          <Input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(e.target.value)
            }
            leadingIcon={<Mail className="size-4" />}
            autoFocus
          />
        </div>

        <button
          type="submit"
          disabled={!email || loading}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-accent py-2.5 text-[13px] font-semibold text-text-inverse transition-opacity hover:opacity-90 disabled:opacity-50"
        >
          {loading ? (
            <span className="size-4 animate-spin rounded-full border-2 border-text-inverse/30 border-t-primary-foreground" />
          ) : (
            <Send className="size-4" />
          )}
          {loading ? "발송 중..." : "재설정 메일 보내기"}
        </button>
      </form>

      <p className="mt-2 text-center text-[12px] text-text-tertiary">
        <Link
          to="/auth/login"
          className="inline-flex items-center gap-1 font-medium hover:text-text-primary"
        >
          <ArrowLeft className="size-3" /> 로그인으로 돌아가기
        </Link>
      </p>
    </AuthTemplate>
  );
}
